import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { intlLocale } from './adminLocale';
import type { components } from '@/api/schema';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Search, TicketCheck } from 'lucide-react';

type Ticket = components['schemas']['TicketResource'];

const statusLabels = (): Record<string, string> => ({
  new: i18n.t('adm_tickets.st_new'), reviewing: i18n.t('adm_tickets.st_reviewing'), resolved: i18n.t('adm_tickets.st_resolved'), closed: i18n.t('adm_tickets.st_closed'),
});
const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800', reviewing: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800', closed: 'bg-muted text-muted-foreground',
};
const priorityLabels = (): Record<string, string> => ({
  low: i18n.t('adm_tickets.pr_low'), medium: i18n.t('adm_tickets.pr_medium'), high: i18n.t('adm_tickets.pr_high'),
});

export default function AdminTickets() {
  const { t: tr } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading } = useQuery({ queryKey: ['/admin/tickets'] });
  const tickets = (data?.data ?? []) as Ticket[];

  const filtered = tickets.filter(t => {
    const matchSearch = !search ||
      t.subject.toLowerCase().includes(search.toLowerCase()) ||
      t.ticket_number.toLowerCase().includes(search.toLowerCase()) ||
      (t.merchant?.name ?? '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{tr('adm_tickets.title')}</h1>
        <p className="text-muted-foreground text-sm">{tr('adm_tickets.subtitle')}</p>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={tr('adm_common.search')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder={tr('adm_tickets.status')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tr('adm_common.all')}</SelectItem>
            <SelectItem value="new">{tr('adm_tickets.st_new')}</SelectItem>
            <SelectItem value="reviewing">{tr('adm_tickets.st_reviewing')}</SelectItem>
            <SelectItem value="resolved">{tr('adm_tickets.st_resolved')}</SelectItem>
            <SelectItem value="closed">{tr('adm_tickets.st_closed')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8">{tr('adm_common.loading')}</div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{tr('adm_tickets.number')}</TableHead>
                <TableHead>{tr('adm_tickets.subject')}</TableHead>
                <TableHead>Merchant</TableHead>
                <TableHead>{tr('adm_tickets.creator')}</TableHead>
                <TableHead>{tr('adm_tickets.priority')}</TableHead>
                <TableHead>{tr('adm_tickets.status')}</TableHead>
                <TableHead>{tr('adm_common.date')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    <TicketCheck className="mx-auto mb-2 h-8 w-8 opacity-30" />
                    {tr('adm_tickets.notFound')}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(t => (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => navigate(`/admin/tickets/${t.id}`)}>
                    <TableCell className="font-mono text-xs">{t.ticket_number}</TableCell>
                    <TableCell className="font-medium">{t.subject}</TableCell>
                    <TableCell>{t.merchant?.name || '—'}</TableCell>
                    <TableCell className="text-sm">{t.created_by_user?.name || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{priorityLabels()[t.priority] || t.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColors[t.status] || ''}>
                        {statusLabels()[t.status] || t.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString(intlLocale())}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
